import { create } from "zustand";
import { CollapsibleState } from "~/components/build/Show/components/Sidebar/TreeItem/CollapsibleState";
import { Step, isGroupStep, isWaitStep } from "~/lib/pipeline";
import { Build, Job } from "~/components/build/Show/lib/types";
import { StepsDisplayMode } from "~/components/build/Show/lib/types/BuildView";
import {
  getRelevantFilterForStep,
  groupHasMatchingChildren,
  sortSteps,
} from "~/components/build/Show/lib/useFilterStore";
import { StateOrder } from "~/components/build/Show/lib/filterConstants";

interface BuildStore {
  steps: Step[];
  stepsMap: Map<string, Step>;
  stepsGroupedByParent: Map<string, Step[]>;
  stepsGroupedByState: Map<string, Step[]>;
  setSteps: (steps: Step[], build: Build) => void;

  jobs: Map<string, Job>;
  stepJobs: Map<string, Job[]>;
  setJobs: (jobs: Job[]) => void;

  /**
   * Expanded sidebar step ids (ie. group/matrix/parallel)
   */
  expandedStepIds: Map<string, CollapsibleState>;
  showHiddenJobs: (id: string) => void;
  expandStep: (id: string) => void;
  toggleStep: (id: string) => void;
  setCollapsibleState: (id: string, state: CollapsibleState) => void;

  /**
   * Tracks user overrides of state header expansion during a filter session.
   * Keyed by state header ID, value includes the filter key to scope overrides
   * to the current filter combination.
   */
  filterStateOverrides: Map<string, { filterKey: string; state: CollapsibleState }>;
  setFilterStateOverride: (
    id: string,
    override: { filterKey: string; state: CollapsibleState },
  ) => void;
  clearFilterStateOverrides: () => void;

  /**
   * Canvas groups
   */
  expandedGroupSteps: Set<string>;
  expandGroupSteps: (uuids: string[]) => void;
  collapseGroupSteps: (uuids: string[]) => void;
  isGroupStepExpanded: (uuid: string) => boolean;

  /**
   * Step search state
   */
  isSearchOpen: boolean;
  setSearchOpen: (open: boolean) => void;

  isDisplayOptionsOpen: boolean;
  setDisplayOptionsOpen: (open: boolean) => void;

  tableSearchValue: string;
  setTableSearchValue: (value: string) => void;

  /**
   * Session-only override for the annotations panel (right-docked) open
   * state. `null` means "use the persisted preference"; a boolean overrides
   * the persisted preference for the rest of this session.
   *
   * Set to `true` on initial render of a build with annotations so the
   * panel auto-opens regardless of the user's persisted preference. Updated
   * by `useAnnotationsPanel` when the user toggles the panel so they can
   * still close it after the auto-open.
   */
  annotationsPanelOverride: boolean | null;
  setAnnotationsPanelOverride: (override: boolean | null) => void;

  /**
   * Current steps display mode for this session.
   * Initialized from defaultView preference; not persisted independently.
   */
  stepsDisplayMode: StepsDisplayMode;
  setStepsDisplayMode: (mode: StepsDisplayMode) => void;
}

/**
 * Store for ephemeral build page-related state.
 */
export const useBuildStore = create<BuildStore>((set, get) => ({
  jobs: new Map<string, Job>(),
  stepJobs: new Map<string, Job[]>(),

  setJobs: (jobs: Job[]) => {
    const jobsMap = new Map([...get().jobs]);
    const stepJobsMap = new Map([...get().stepJobs]);

    for (const job of jobs) {
      const existingJob = jobsMap.get(job.id);

      if (existingJob) {
        // Update the existing job in both maps
        jobsMap.set(job.id, job);
        const stepJobs = stepJobsMap.get(job.stepUuid) || [];
        const jobIndex = stepJobs.findIndex((_job) => _job.id === job.id);
        if (jobIndex !== -1) {
          stepJobs[jobIndex] = job;
        }
      } else {
        // Add new job
        const stepJobs = stepJobsMap.get(job.stepUuid) || [];
        stepJobs.push(job);
        jobsMap.set(job.id, job);
        stepJobsMap.set(job.stepUuid, stepJobs);
      }
    }

    set({ jobs: jobsMap, stepJobs: stepJobsMap });
  },

  steps: [],
  stepsMap: new Map(),
  stepsGroupedByParent: new Map(),
  stepsGroupedByState: new Map(),

  /**
   * Add/replace steps in the store whilst also grouping them by state.
   *
   * `Build` is required for determining the "resolved state" of each step.
   *
   * todo: Handle replacing steps (via `--replace`)
   */
  setSteps: (input: Step[], _build: Build) => {
    const stepsMap = get().stepsMap;
    const stepsGroupedByParent = new Map<string, Step[]>();
    const stepsGroupedByState = new Map<string, Step[]>();

    let hasAddedSteps = false;

    for (const step of input) {
      if (!stepsMap.has(step.uuid)) {
        hasAddedSteps = true;
      }
      stepsMap.set(step.uuid, step);
    }

    const steps = Array.from(stepsMap.values());
    const sortedStepsMap = new Map<string, Step>();

    // Since the store can have a subset of the steps, when new steps are added in (ie. switch display modes, expanding groups),
    // we need to re-sort to ensure they're in the correct order.
    if (hasAddedSteps) {
      sortSteps(steps);
    }

    /**
     * Iterate over all steps and group them.
     */
    steps.forEach((step) => {
      if (hasAddedSteps) {
        sortedStepsMap.set(step.uuid, step);
      }

      // Group steps by parent.
      if (step.groupUuid) {
        const children = stepsGroupedByParent.get(step.groupUuid) || [];
        children.push(step);
        stepsGroupedByParent.set(step.groupUuid, children);
      }

      // Group steps by state.
      if (isWaitStep(step)) {
        // Skip wait steps entirely.
      } else if (isGroupStep(step)) {
        // Add group step to each state bucket where it has matching children.
        for (const filter of StateOrder) {
          if (groupHasMatchingChildren(step, [filter])) {
            if (!stepsGroupedByState.has(filter)) {
              stepsGroupedByState.set(filter, []);
            }
            stepsGroupedByState.get(filter)?.push(step);
          }
        }
      } else {
        // Include all leaf steps (both grouped and ungrouped) for backward
        // compatibility with the sidebar. Consumers that show groups filter
        // out grouped leaf steps themselves.
        const state = getRelevantFilterForStep(step);
        if (state) {
          if (!stepsGroupedByState.has(state)) {
            stepsGroupedByState.set(state, []);
          }
          stepsGroupedByState.get(state)?.push(step);
        }
      }
    });

    set({
      steps,
      stepsMap: hasAddedSteps ? sortedStepsMap : stepsMap,
      stepsGroupedByParent,
      stepsGroupedByState,
    });
  },

  isSearchOpen: false,
  setSearchOpen: (open: boolean) => set(() => ({ isSearchOpen: open })),
  isDisplayOptionsOpen: false,
  setDisplayOptionsOpen: (open: boolean) => set(() => ({ isDisplayOptionsOpen: open })),
  tableSearchValue: "",
  setTableSearchValue: (value: string) => set(() => ({ tableSearchValue: value })),

  annotationsPanelOverride: null,
  setAnnotationsPanelOverride: (override: boolean | null) =>
    set(() => ({ annotationsPanelOverride: override })),

  stepsDisplayMode: StepsDisplayMode.Canvas,
  setStepsDisplayMode: (mode: StepsDisplayMode) => set(() => ({ stepsDisplayMode: mode })),

  setCollapsibleState: (id: string, collapsibleState: CollapsibleState) => {
    set((state) => {
      const expandedStepIds = new Map(state.expandedStepIds);
      expandedStepIds.set(id, collapsibleState);
      return { expandedStepIds };
    });
  },
  filterStateOverrides: new Map(),
  setFilterStateOverride: (
    id: string,
    override: { filterKey: string; state: CollapsibleState },
  ) => {
    set((state) => {
      const filterStateOverrides = new Map(state.filterStateOverrides);
      filterStateOverrides.set(id, override);
      return { filterStateOverrides };
    });
  },
  clearFilterStateOverrides: () => set({ filterStateOverrides: new Map() }),
  expandedStepIds: new Map<string, CollapsibleState>(),
  showHiddenJobs: (id: string) => {
    set((state) => {
      const expandedStepIds = new Map(state.expandedStepIds);
      expandedStepIds.set(id, CollapsibleState.Full);
      return { expandedStepIds };
    });
  },
  expandStep: (id: string) => {
    set((state) => {
      const expandedStepIds = new Map(state.expandedStepIds);
      if (expandedStepIds.get(id) !== CollapsibleState.Full) {
        expandedStepIds.set(id, CollapsibleState.Expanded);
      }
      return { expandedStepIds };
    });
  },
  toggleStep: (id: string) => {
    set((state) => {
      const expandedStepIds = new Map(state.expandedStepIds);
      expandedStepIds.set(
        id,
        expandedStepIds.get(id) === CollapsibleState.Expanded ||
          expandedStepIds.get(id) === CollapsibleState.Full
          ? CollapsibleState.Collapsed
          : CollapsibleState.Expanded,
      );
      return { expandedStepIds };
    });
  },
  expandedGroupSteps: new Set<string>(),

  expandGroupSteps: (uuids: string[]) => {
    set((state) => {
      const expandedGroupSteps = new Set(state.expandedGroupSteps);
      uuids.forEach((uuid) => expandedGroupSteps.add(uuid));
      return { expandedGroupSteps };
    });
  },

  collapseGroupSteps: (uuids: string[]) => {
    set((state) => {
      const expandedGroupSteps = new Set(state.expandedGroupSteps);
      uuids.forEach((uuid) => expandedGroupSteps.delete(uuid));
      return { expandedGroupSteps };
    });
  },

  isGroupStepExpanded: (uuid: string) => {
    return get().expandedGroupSteps.has(uuid);
  },
}));
